import { Box, Paper, Stack } from '@mui/material'
import type { ReactNode } from 'react'

import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type { BidDetailQueryParams } from '@/features/bidding/hooks/bidDetail/useBidDetailQueryParams'

import type { ActivationGroupOption } from './ActivationGroupMultiSelect'
import ActivationGroupMultiSelect from './ActivationGroupMultiSelect'
import BidDatePicker from './BidDatePicker'
import BiddingVersionsButton from './BiddingVersionsButton'
import type { PtuRange } from './PtuRangeSelector'
import PtuRangeSelector from './PtuRangeSelector'
import RefreshButton from './RefreshButton'

export type BidDetailToolbarProps = Readonly<{
  // Context (from URL)
  marketProgram: MarketProgram
  bidType: BidType
  queryParams: BidDetailQueryParams

  // Activation groups (provided by the per-tab view)
  activationGroupOptions: ReadonlyArray<ActivationGroupOption>
  selectedActivationGroupUuids: ReadonlyArray<string>
  onActivationGroupSelectionChange: (next: ReadonlyArray<string>) => void

  // Bid date (URL-synced — owner sets via useBidDetailQueryParams)
  onBidDateChange: (next: BidDetailQueryParams['deliveryDay']) => void

  // PTU range (local state owned by the per-tab view)
  ptuRange: PtuRange
  onPtuRangeChange: (next: PtuRange) => void
  /**
   * Fired when the user clicks "Now → End" in the PTU range selector.
   * The view is expected to (a) set the bid date to today and (b) reset
   * the PTU range to "Now → End" of today. See `BidEnergyView`.
   */
  onResetToNow: () => void

  // Refresh
  onRefresh: () => void
  isRefreshing?: boolean

  // Slot for view-specific extras (e.g. an Energy-only filter, if needed later)
  extraFilters?: ReactNode
}>

/**
 * Filter row for the new bid detail page. Generic shell — knows nothing
 * about which bid type is rendering. All state is owned by the per-tab view
 * (e.g. {@link BidEnergyView}); this component is purely presentational.
 */
export default function BidDetailToolbar({
  marketProgram,
  bidType,
  queryParams,
  activationGroupOptions,
  selectedActivationGroupUuids,
  onActivationGroupSelectionChange,
  onBidDateChange,
  ptuRange,
  onPtuRangeChange,
  onResetToNow,
  onRefresh,
  isRefreshing = false,
  extraFilters,
}: BidDetailToolbarProps) {
  return (
    <Paper elevation={0} sx={{ borderRadius: 1, padding: 2 }} variant="outlined">
      <Stack useFlexGap alignItems="flex-end" direction="row" flexWrap="wrap" spacing={2} sx={{ rowGap: 2 }}>
        <ActivationGroupMultiSelect
          options={activationGroupOptions}
          selectedUuids={selectedActivationGroupUuids}
          onChange={onActivationGroupSelectionChange}
        />

        <BidDatePicker value={queryParams.deliveryDay} onChange={onBidDateChange} />

        {queryParams.deliveryDay && (
          <PtuRangeSelector
            deliveryDay={queryParams.deliveryDay}
            range={ptuRange}
            onChange={onPtuRangeChange}
            onResetToNow={onResetToNow}
          />
        )}

        {extraFilters}

        <Box sx={{ flexGrow: 1 }} />

        <Stack alignItems="center" direction="row" spacing={1}>
          <RefreshButton isRefreshing={isRefreshing} onRefresh={onRefresh} />
          <BiddingVersionsButton bidType={bidType} marketProgram={marketProgram} queryParams={queryParams} />
        </Stack>
      </Stack>
    </Paper>
  )
}
